import { ReactNode, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMobileHealth } from '../hooks/useMobileHealth';
import { useMobileStats, getDateRangeFromPreset, fetchAllJobsInRange } from '../hooks/useMobileStats';
import { BatchRunsTable } from '../components/BatchRunsTable';
import { CompletionChart, DuplicationCard, DurationStats, RequestAnalytics } from '../../components/stats';
import type { AnalyticsJob } from '../../components/stats';
import type { DateRange } from '../types';

interface KpiCardProps {
  label: string;
  value: number | string;
  tone: 'amber' | 'slate' | 'green' | 'red' | 'indigo';
  icon: ReactNode;
  hint?: string;
}

const TONE_STYLES: Record<KpiCardProps['tone'], { bg: string; iconBg: string; iconText: string; valueText: string }> = {
  amber: {
    bg: 'bg-white',
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-600',
    valueText: 'text-slate-900',
  },
  slate: {
    bg: 'bg-white',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
    valueText: 'text-slate-900',
  },
  green: {
    bg: 'bg-white',
    iconBg: 'bg-green-50',
    iconText: 'text-green-600',
    valueText: 'text-slate-900',
  },
  red: {
    bg: 'bg-white',
    iconBg: 'bg-red-50',
    iconText: 'text-red-600',
    valueText: 'text-slate-900',
  },
  indigo: {
    bg: 'bg-white',
    iconBg: 'bg-indigo-50',
    iconText: 'text-indigo-600',
    valueText: 'text-slate-900',
  },
};

function KpiCard({ label, value, tone, icon, hint }: KpiCardProps) {
  const t = TONE_STYLES[tone];
  return (
    <div
      className={`${t.bg} rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">{label}</div>
          <div className={`mt-2 text-3xl font-semibold tracking-tight ${t.valueText}`}>{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl border border-slate-200 ${t.iconBg} ${t.iconText} flex items-center justify-center`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

export function MobileOverviewPage() {
  const { health } = useMobileHealth();
  const dateRange = useMemo(() => getDateRangeFromPreset('last7days'), []);
  const { stats, loading } = useMobileStats(dateRange);

  const fetchAnalyticsJobs = useCallback(
    async (range: DateRange): Promise<AnalyticsJob[]> => {
      const jobs = await fetchAllJobsInRange(range);
      return jobs.map((j) => ({
        id: j.job_id,
        created_at: j.created_at,
        status: j.status,
        duration_seconds: j.duration_seconds,
        error: j.error,
      }));
    },
    [],
  );

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Active"
          value={health?.active_jobs ?? '—'}
          tone="amber"
          hint="Currently processing"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <KpiCard
          label="Pending"
          value={health?.pending_jobs ?? '—'}
          tone="slate"
          hint="Waiting in queue"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <KpiCard
          label="Complete"
          value={health?.completed_jobs ?? '—'}
          tone="green"
          hint="Today"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          }
        />
        <KpiCard
          label="Failed"
          value={health?.failed_jobs ?? '—'}
          tone="red"
          hint="Today"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          }
        />
      </div>

      {/* Request analytics */}
      <RequestAnalytics fetchJobs={fetchAnalyticsJobs} statsHref="/lightning/stats" />

      {/* Active batches */}
      <BatchRunsTable />

      {/* Last 7 days mini stats */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Last 7 days</h2>
            <p className="text-sm text-slate-500">Completion rate, duplicate submissions and processing duration</p>
          </div>
          <Link
            to="/lightning/stats"
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors"
          >
            View full statistics
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {loading && !stats ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Loading statistics…
          </div>
        ) : stats && stats.completion.total > 0 ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
              <DuplicationCard stats={stats.duplication} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
                <CompletionChart stats={stats.completion} title="Completion" />
              </div>
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
                <DurationStats stats={stats.duration} />
              </div>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center">
            <div className="inline-flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
                <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-medium">No data yet</p>
                <p className="text-sm text-slate-500">Statistics will appear after the first scans complete</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
